import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';

export type EmailType = 'confirm' | 'cancel' | 'follow_up' | 'reminder' | 'pre_meeting';
interface UpdateEmailTypeSettingsParams {
  type: EmailType;
  settings: Record<string, any>;
}

export function useAllEmailSettings() {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['emailSettings', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('email_settings')
        .select('settings')
        .eq('user_id', user.id)
        .single();
    
      if (error) {
        console.error('Error fetching all email settings:', error);
        throw error;
      }
      
      return data.settings;
    },
    enabled: !!user?.id
  });
}

export function useEmailTypeSettings(type: EmailType) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['emailSettings', user?.id, type],
    queryFn: async () => {
      if (!user?.id) return console.log('❌ No user ID available');
      
      try {
        const { data, error } = await supabase
        .from('email_settings')
        .select('settings')
        .eq('user_id', user.id)
        .single();
    
      if (error) {
        console.error(`Error fetching email settings:`, error);
        throw error;
      }
        return data.settings;
      } catch (error) {
        console.error(`❌ Error fetching email settings:`, error);
        throw error;
      }
    },
    enabled: !!user?.id
  });
}

export function useUpdateEmailTypeSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ type, settings }: UpdateEmailTypeSettingsParams) => {
      if (!user?.id) throw new Error('User ID is required');

      const { data: currentData, error: fetchError } = await supabase
        .from('email_settings')
        .select('settings')
        .eq('user_id', user.id)
        .single();

      if (fetchError) {
        console.error(`Error fetching current settings:`, fetchError);
        throw fetchError;
      }

      const updatedSettings = {
        ...currentData.settings,
        [type]: {
          ...currentData.settings[type],
          ...settings
        }
      };

      const { data, error } = await supabase
        .from('email_settings')
        .update({ 
          settings: updatedSettings,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error(`Error updating ${type} email settings:`, error);
        throw error;
      }

      return data.settings[type];
    },
    onSuccess: (_, { type }) => {
      // Инвалидируем кеш для конкретного типа email
      queryClient.invalidateQueries({ 
        queryKey: ['emailSettings', user?.id, type] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['emailSettings', user?.id] 
      });
      
      console.log(`🔄 Cache invalidated for ${type} email settings`);
    },
    onError: (error, { type }) => {
      console.error(`❌ Failed to update ${type} email settings:`, error);
    }
  });
}